import { Children, cloneElement, FC, isValidElement, ReactNode } from 'react'

import { Tab } from '@headlessui/react'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

import { useBackgroundContext } from '../BackgroundContext/index.js'
import { Icon } from '../Icon/index.js'
import type { IconComponentType } from '../Icon/types.js'
import { HTMLTribeProps, As } from '../types/index.js'
import { TabSize, TabsProvider, TabVariant, useTabs } from './TabsContext.js'

/**
 * An accessible tabs component that provides keyboard interactions and ARIA attributes described in the WAI-ARIA Tabs Design Pattern.
 */
export const Tabs = () => null

export type TabGroupProps = {
  /**
   * The component that you want to use
   * @default Tab.Group
   */
  as?: As
  /**
   * The default selected index
   * @default 0
   */
  defaultIndex?: number
  /**
   * The selected index, by default it is determined based on user selection
   * @default 0
   */
  selectedIndex?: number
  /**
   * A function called whenever the active tab changes.
   */
  onChange?: (index: number) => void
  /**
   * When true, the user can only display a panel via the keyboard by first navigating to it using the arrow keys, and then by pressing Enter or Space.
   * By default, panels are automatically displayed when navigated to via the arrow keys. Note that this prop has no affect on mouse behavior.
   * @default false
   */
  manual?: boolean
  children?: ReactNode
}

const TabGroup: FC<TabGroupProps> = props => {
  const { as: Component = Tab.Group, children, ...rest } = props

  return <Component {...rest}>{children}</Component>
}

export type TabListProps = React.ComponentProps<'div'> & {
  as?: As
  /**
   * The variant of the tabs
   */
  variant?: TabVariant
  size?: TabSize
  fullWidth?: boolean
  shadow?: boolean
  /**
   * 'none' - no border radius on mobile and desktop
   * 'desktop' - no border radius on mobile, rounded on desktop
   * 'all' - rounded on both mobile and desktop
   * @default all
   */
  rounded?: 'none' | 'desktop' | 'all'
  divide?: boolean
  attached?: 'bottom' | 'top' | 'none'
}

const TabList: FC<TabListProps> = props => {
  const {
    as: Component = Tab.List,
    children,
    className,
    variant = 'bar',
    size = 'md',
    fullWidth = false,
    shadow = true,
    rounded = 'desktop',
    divide = true,
    attached = 'none',
    ...rest
  } = props

  const barClsx = [
    'z-0',
    divide && 'divide-x divide-line-subdued',
    shadow && 'shadow',
    rounded === 'none' && 'rounded-t-none rounded-b-none',
    rounded === 'desktop' &&
      'rounded-t-none rounded-b-none sm:rounded-t-lg sm:rounded-b-lg',
    rounded === 'all' && 'rounded-t-lg rounded-b-lg',
  ]

  let attachedClsx = ''
  if (attached === 'top') {
    attachedClsx =
      'rounded-t-none sm:rounded-t-none border-t border-t-line-subdued'
  }
  if (attached === 'bottom') {
    attachedClsx =
      'rounded-b-none sm:rounded-b-none border-b border-b-line-subdued'
  }

  return (
    <TabsProvider variant={variant} size={size} fullWidth={fullWidth}>
      <Component
        className={twMerge(
          clsx(
            'overflow-x-auto isolate relative',
            variant === 'bar' && barClsx,
            variant === 'bar' && attachedClsx,
            variant === 'pills' && 'space-s-4 mx-2 sm:mx-0',
            variant === 'pills-accent' && 'space-s-4 mx-2 sm:mx-0',
            !fullWidth && 'inline-flex flex-nowrap',
            fullWidth && 'flex  flex-nowrap',
            className,
          ),
        )}
        aria-label="Tabs"
        {...rest}
      >
        {({ selectedIndex }) => {
          // workaround for https://github.com/tailwindlabs/headlessui/issues/1488
          return Children.toArray(children).map((child, index) => {
            if (isValidElement(child)) {
              return cloneElement<{ selected?: boolean }>(child, {
                selected: index === selectedIndex,
              })
            }
            return child
          })
        }}
      </Component>
    </TabsProvider>
  )
}

export type TabItemProps = HTMLTribeProps<'button'> & {
  as?: As
  selected?: boolean
  leadingIcon?: IconComponentType
}

const TabItem: FC<TabItemProps> = props => {
  const {
    as: Component = Tab,
    children,
    leadingIcon,
    className: customClassName,
    disabled,
    selected: customSelected,
    ...rest
  } = props
  const { variant, size, fullWidth } = useTabs()
  const { backgroundType } = useBackgroundContext()

  const variantBar = selected => [
    selected
      ? 'text-content'
      : 'text-content-subdued hover:text-content-hovered',
    'group relative text-center',
    'bg-surface  hover:bg-surface-hovered',
    size === 'md' && 'py-4 px-4 m-0 text-sm',
    size === 'sm' && 'py-3 px-2 m-0 text-sm leading-4',
  ]

  const variantPills = selected => [
    [
      backgroundType === 'main' && [
        selected
          ? 'bg-background-pressed text-content-on-background-subdued'
          : 'text-content-on-background-subdued hover:text-content-on-background-hovered',
      ],
      backgroundType === 'surface' && [
        selected
          ? 'bg-surface-pressed text-content-subdued'
          : 'text-content-subdued hover:text-content-hovered',
      ],
    ],
    'rounded-md',
    size === 'md' && 'px-3 py-1.5',
    size === 'sm' && 'px-2 py-1.5 text-sm leading-4',
    'focus-visible:ring ring-inset ring-offset-0',
  ]

  const variantPillsAccent = selected => [
    selected
      ? 'bg-action-primary-subdued text-content-primary'
      : 'text-content-subdued hover:text-content-hovered',
    'rounded-md',
    size === 'md' && 'px-3 py-1.5',
    size === 'sm' && 'px-2 py-1.5 text-sm leading-4',
    'focus-visible:ring ring-inset ring-offset-0',
  ]

  let className: any = ({ selected }) =>
    clsx(
      !fullWidth && 'flex-grow-0 flex-shrink-0',
      fullWidth && 'flex-1',
      variant === 'bar' && variantBar(selected),
      variant === 'pills' && variantPills(selected),
      variant === 'pills-accent' && variantPillsAccent(selected),
      'focus:outline-none focus:z-10',
      disabled && 'opacity-50 cursor-default',
      customClassName,
    )
  let content: any = ({ selected }) => (
    <>
      {leadingIcon && (
        <Icon
          className={clsx(
            selected
              ? 'text-action-primary'
              : 'text-content-subdued group-hover:text-content-hovered',
            'inline-block -ms-0.5 me-2 h-5 w-5',
          )}
        >
          {leadingIcon}
        </Icon>
      )}
      {children}
      {variant === 'bar' && (
        <span
          aria-hidden="true"
          className={clsx(
            selected ? 'bg-action-primary' : 'bg-transparent',
            'absolute inset-x-0 bottom-0 h-0.5',
          )}
        />
      )}
    </>
  )

  if (customSelected !== undefined) {
    className = className({ selected: customSelected })
    content = content({ selected: customSelected })
  }

  return (
    <Component className={className} disabled={disabled} {...rest}>
      {content}
    </Component>
  )
}

export type TabPanelsSpacing = 'sm' | 'md' | 'lg' | 'none'

export type TabPanelsProps = React.ComponentProps<'div'> & {
  as?: As
  spacing?: TabPanelsSpacing
}

const TabPanels: FC<TabPanelsProps> = props => {
  const {
    as: Component = Tab.Panels,
    spacing = 'md',
    children,
    className,
    ...rest
  } = props

  return (
    <div
      className={clsx(
        [
          spacing === 'lg' && 'mt-5 sm:mt-6 md:mt-7 lg:mt-8',
          spacing === 'md' && 'mt-3',
          spacing === 'sm' && 'mt-2 sm:mt-2.5 lg:mt-3',
          spacing === 'none' && 'mt-0',
        ],
        className,
      )}
      {...rest}
    >
      <Component>
        {({ selectedIndex }) => {
          // workaround for https://github.com/tailwindlabs/headlessui/issues/1488
          return Children.toArray(children).map((child, index) => {
            if (index !== selectedIndex) {
              return null
            }
            return child
          })
        }}
      </Component>
    </div>
  )
}
const TabPanel = props => {
  const { children, className, ...rest } = props

  return (
    <Tab.Panel
      className={clsx('focus:outline-none focus-visible:ring', className)}
      {...rest}
      static
    >
      {children}
    </Tab.Panel>
  )
}

Tabs.Group = TabGroup
Tabs.List = TabList
Tabs.Tab = TabItem
Tabs.Panels = TabPanels
Tabs.Panel = TabPanel
